import { useEditorMaybe } from '@grapesjs/react';
import { Box, Card, CardActionArea, CardContent, Divider, TextField, Tooltip } from '@mui/material';
import { RiCheckLine, RiDownload2Line, RiDownloadLine, RiUploadLine } from '@remixicon/react';
import { useNavigate } from '@tanstack/react-router';
import { saveAs } from 'file-saver';
import JSZip from 'jszip';
import { useEffect, useMemo, useRef, useState } from 'react';
import DocumentGenerator from 'shared/documents/DocumentGenerator';
import { AgButton, AgModal, AgModalActions, AgModalContent, AgText } from 'src/components/ui';
import { appLinks } from 'src/lib/routing';
import convertToTitleCase from 'src/utils/convertToTitleCase';

import {
  type DocumentOptions,
  documentTypeGroups,
  documentTypeOptions,
  type GenerationOptions,
} from '../pages/DocumentEditorPage/documentEditorOptions';
import { type LoadableTemplate, useDocumentTemplateLoadPage } from './useDocumentTemplateLoadPage';
import { useReadAllDocumentTemplates } from './useReadAllDocumentTemplates';

type DocumentSections = Record<string, LoadableTemplate[]>;

const SEARCH_DEBOUNCE_MS = 300;

/** Appends each template to its entity's section, after what the section already holds. */
const withTemplates = (sections: DocumentSections, templates: LoadableTemplate[]): DocumentSections => {
  const next: DocumentSections = { ...sections };
  for (const template of templates) next[template.entityType] = [...(next[template.entityType] ?? []), template];
  return next;
};

export default function DocumentStorageLoadModal({
  open,
  onClose,
  setDocumentName,
  setDocumentType,
  setGenerationOptions,
  setShowInBulkPrintMenu,
}: {
  open: boolean;
  onClose: () => void;
  setDocumentName: (name: string) => void;
  setDocumentType: (type: Pick<DocumentOptions, 'label'> & { entity: string; type: string }) => void;
  setGenerationOptions: (options: GenerationOptions | null) => void;
  setShowInBulkPrintMenu: (show: boolean) => void;
}) {
  const editor = useEditorMaybe();
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { readAllDocumentTemplates } = useReadAllDocumentTemplates();
  const [selectedTemplate, setSelectedTemplate] = useState<LoadableTemplate | null>(null);
  const [defaultSections, setDefaultSections] = useState<DocumentSections>({});
  const [imported, setImported] = useState<LoadableTemplate[]>([]);
  const [searchText, setSearchText] = useState('');
  const [term, setTerm] = useState('');

  useEffect(() => {
    const timer = setTimeout(() => setTerm(searchText.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchText]);

  // One page of the org's templates per open (P7-R35); "Show more" appends the next page after the last cursor.
  const { templates: orgTemplates, hasNextPage, fetchMore } = useDocumentTemplateLoadPage(term);

  useEffect(() => {
    const fetchDefaults = async () => {
      const sections: DocumentSections = {};

      for (const { type, entity } of documentTypeOptions) {
        const defaultForType = DocumentGenerator.defaultTemplates[entity]?.[type];

        if (!defaultForType) {
          continue;
        }

        const defaultTemplate = await defaultForType();
        const templateMetadata = { id: `template-${entity}-${type}`, isTemplate: true };

        if (!sections[entity]) {
          sections[entity] = defaultTemplate ? [{ ...templateMetadata, ...defaultTemplate }] : [];
        } else {
          sections[entity].push({ ...templateMetadata, ...defaultTemplate });
        }
      }

      return sections;
    };

    fetchDefaults().then((sections) => setDefaultSections(sections));
  }, []);

  // Agriful defaults first, then the org's templates (server-searched), then imported files, as before.
  const documentSections = useMemo(() => {
    const needle = term.toLowerCase();
    const defaults = Object.fromEntries(
      Object.entries(defaultSections).map(([entity, templates]) => [
        entity,
        needle ? templates.filter((template) => template.name?.toLowerCase().includes(needle)) : templates,
      ])
    );
    return withTemplates(withTemplates(defaults, orgTemplates), imported);
  }, [defaultSections, orgTemplates, imported, term]);

  const handleClose = () => {
    setSelectedTemplate(null);
    onClose();
  };

  const handleExportSingle = async (template: LoadableTemplate) => {
    // Don't export system default templates
    if (template.isTemplate) {
      alert('Cannot export system default templates');
      return;
    }

    const exportData = {
      name: template.name,
      type: template.type,
      entityType: template.entityType,
      templateState: template.templateState,
      generationOptions: template.generationOptions,
      isTemplate: false,
    };

    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
    saveAs(blob, `${template.name.replace(/\s+/g, '_')}_document.json`);
  };

  const handleExportAll = async () => {
    const zip = new JSZip();
    const customTemplates = [...(await readAllDocumentTemplates()), ...imported];

    if (customTemplates.length === 0) {
      alert('No custom templates to export');
      return;
    }

    for (const template of customTemplates) {
      const exportData = {
        name: template.name,
        type: template.type,
        entityType: template.entityType,
        templateState: template.templateState,
        generationOptions: template.generationOptions,
        isTemplate: false,
      };

      const fileName = `${template.entityType}/${template.name.replace(/\s+/g, '_')}.json`;
      zip.file(fileName, JSON.stringify(exportData, null, 2));
    }

    const content = await zip.generateAsync({ type: 'blob' });
    saveAs(content, 'document_templates.zip');
  };

  const handleImport = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    const file = files[0];
    const fileName = file.name.toLowerCase();

    try {
      if (fileName.endsWith('.json')) {
        const text = await file.text();
        const importedDoc = JSON.parse(text);

        if (!importedDoc.name || !importedDoc.type || !importedDoc.entityType) {
          throw new Error('Invalid document format');
        }

        const newDoc = { ...importedDoc, id: `imported-${Date.now()}-${Math.random()}` };

        setImported((previous) => [...previous, newDoc]);
      } else if (fileName.endsWith('.zip')) {
        const zip = await JSZip.loadAsync(file);
        const newDocs: LoadableTemplate[] = [];

        for (const [path, zipEntry] of Object.entries(zip.files)) {
          if (!zipEntry.dir && path.endsWith('.json')) {
            const text = await zipEntry.async('text');
            const importedDoc = JSON.parse(text);

            if (!importedDoc.name || !importedDoc.type || !importedDoc.entityType) {
              continue;
            }

            newDocs.push({ ...importedDoc, id: `imported-${Date.now()}-${Math.random()}` });
          }
        }

        setImported((previous) => [...previous, ...newDocs]);
      }
    } catch (error) {
      console.error('Error importing document:', error);
      alert('Failed to import document. Please check the file format.');
    }

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <AgModal open={open} onClose={handleClose} title="Select document to open" size="lg">
      <AgModalContent>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', mb: 2 }}>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Tooltip title="Import Document(s)">
              <AgButton variant="ghost" size="icon-sm" onPress={() => fileInputRef.current?.click()}>
                <RiUploadLine size={20} />
              </AgButton>
            </Tooltip>
            <Tooltip
              title={
                selectedTemplate
                  ? selectedTemplate.isTemplate
                    ? 'Cannot export system templates'
                    : 'Export Selected Document'
                  : 'Export All Custom Documents'
              }
            >
              <span>
                <AgButton
                  variant="ghost"
                  size="icon-sm"
                  isDisabled={selectedTemplate && selectedTemplate.isTemplate}
                  onPress={() => (selectedTemplate ? handleExportSingle(selectedTemplate) : handleExportAll())}
                >
                  {selectedTemplate ? <RiDownloadLine size={20} /> : <RiDownload2Line size={20} />}
                </AgButton>
              </span>
            </Tooltip>
          </Box>
        </Box>
        <input ref={fileInputRef} type="file" accept=".json,.zip" style={{ display: 'none' }} onChange={handleImport} />
        <TextField
          size="small"
          fullWidth
          placeholder="Search documents"
          value={searchText}
          onChange={(event) => setSearchText(event.target.value)}
        />
        <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
          {Object.entries(documentSections).map(([entity, templates]) => {
            if (templates?.length === 0) {
              return null;
            }

            return (
              <Box key={entity}>
                <AgText size="14">{documentTypeGroups[entity]} Documents</AgText>
                <Divider />
                <Box sx={{ p: 1, display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                  {templates.map((template) => {
                    const isSelected = selectedTemplate?.id === template.id;

                    return (
                      <Card key={template.id || template.name}>
                        <CardActionArea
                          onClick={() => {
                            setSelectedTemplate({ ...template, entityType: entity });
                          }}
                        >
                          <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                              <AgText size="18" weight="semibold">
                                {template.name} {template?.isTemplate && ' (Agriful)'}
                              </AgText>
                              {isSelected && <RiCheckLine size={20} className="text-success-main" />}
                            </Box>
                            <div className="flex gap-1 mt-1">
                              <span className="badge">{convertToTitleCase(template.type)}</span>
                              {template.id?.toString().startsWith('imported-') && (
                                <span className="badge badge-blue">Imported</span>
                              )}
                            </div>
                          </CardContent>
                        </CardActionArea>
                      </Card>
                    );
                  })}
                </Box>
              </Box>
            );
          })}
          {hasNextPage && (
            <AgButton variant="ghost" onPress={() => void fetchMore()}>
              Show more
            </AgButton>
          )}
        </Box>
      </AgModalContent>
      <AgModalActions>
        <AgButton
          variant="primary"
          isDisabled={!selectedTemplate}
          onPress={() => {
            if (!selectedTemplate || !editor) {
              return;
            }

            const template =
              typeof selectedTemplate.templateState === 'string'
                ? JSON.parse(selectedTemplate.templateState)
                : selectedTemplate.templateState;

            editor.loadProjectData(template);

            setDocumentName(selectedTemplate.name);
            editor.trigger('documentTypeChanged', selectedTemplate.entityType);
            setDocumentType({
              entity: selectedTemplate.entityType,
              type: selectedTemplate.type,
              label: convertToTitleCase(selectedTemplate.type),
            });
            setGenerationOptions(selectedTemplate.generationOptions ?? null);
            setShowInBulkPrintMenu(!!selectedTemplate.showInBulkPrintMenu);

            void navigate(
              appLinks.documentEditor({
                name: selectedTemplate.name,
                type: selectedTemplate.type,
                entity: selectedTemplate.entityType,
              })
            );

            setSelectedTemplate(null);
            onClose();
          }}
        >
          Open
        </AgButton>
      </AgModalActions>
    </AgModal>
  );
}
